import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { refreshPendingCounts } from 'layouts/pending-counts';
import { HP_LOGO_URL, readNotifyScope, showDesktopNotice } from './desktop-notify';
import { playChime, readSoundPref } from './utils';

interface NoticeItem {
  id: number;
  threadId: number;
  userName: string;
  userClassLabel: string | null;
  preview: string;
  assigneeId: number | null;
}

const POLL_MS = 10_000;
/** Chống báo trùng khi mở admin ở nhiều tab: tab nào báo trước thì ghi mốc lại. */
const SHARED_KEY = 'support.notified.lastId';

function readShared(): number {
  try {
    return Number(localStorage.getItem(SHARED_KEY)) || 0;
  } catch {
    return 0;
  }
}
function writeShared(id: number) {
  try {
    localStorage.setItem(SHARED_KEY, String(id));
  } catch {
    // bỏ qua
  }
}

/**
 * Nhịp hỏi tin mới chạy trong Web Worker: Chrome hãm setInterval của tab nền
 * xuống 1 lần/phút sau 5 phút ẩn, còn timer trong worker thì không → thông báo
 * vẫn đến sau ~10 giây dù tab admin đã ở nền lâu. Không tạo được worker thì
 * quay về setInterval thường.
 */
function startTicker(onTick: () => void): () => void {
  try {
    const code = `setInterval(function(){postMessage(0)}, ${POLL_MS});`;
    const url = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
    const worker = new Worker(url);
    worker.onmessage = onTick;
    return () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  } catch {
    const timer = window.setInterval(onTick, POLL_MS);
    return () => window.clearInterval(timer);
  }
}

/**
 * Chạy ở MỌI trang admin (gắn trong layouts/layout.tsx): có người nhắn "HP Media
 * Hỗ trợ" → popup nhỏ góc màn hình máy tính (logo HP, tên, tóm tắt) + âm báo;
 * bấm vào mở đúng cuộc trò chuyện. Mặc định báo mọi tin; chọn "chỉ của tôi" thì
 * chỉ báo luồng chưa ai nhận hoặc đang giao cho mình.
 * Chưa cho phép thông báo máy tính → hiện thẻ nhỏ góc phải trong trang thay thế.
 */
const SupportNotifier = () => {
  const { userId } = useCurrentRoles();
  const navigate = useNavigate();
  const location = useLocation();
  const cursor = useRef<number | null>(null);
  const busy = useRef(false);
  const locationRef = useRef(location);
  locationRef.current = location;
  const meRef = useRef(userId);
  meRef.current = userId;
  // useNavigate đổi hàm theo route → giữ trong ref để bộ hỏi tin không khởi động lại mỗi lần chuyển trang.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let stopped = false;

    const open = (threadId: number) => navigateRef.current(`/support?box=all&t=${threadId}`);

    const notify = (item: NoticeItem) => {
      const loc = locationRef.current;
      const params = new URLSearchParams(loc.search);
      // Đang mở đúng cuộc này và đang nhìn màn hình → không cần báo.
      if (
        document.visibilityState === 'visible' &&
        loc.pathname.replace(/\/+$/, '') === '/support' &&
        Number(params.get('t')) === item.threadId
      ) {
        return;
      }
      const title = item.userClassLabel ? `${item.userName} · ${item.userClassLabel}` : item.userName;
      const body = item.preview || 'Đã gửi một tin nhắn';
      const sound = readSoundPref();
      // Có âm báo riêng của trang (đã được phép phát) → tắt tiếng popup để không kêu đôi;
      // chưa có quyền phát âm thanh (vừa tải lại, chưa bấm gì) → để hệ điều hành kêu.
      const pageCanPlay = !!(navigator as any).userActivation?.hasBeenActive;
      const shown = showDesktopNotice({
        title,
        body,
        tag: `support-thread-${item.threadId}`,
        silent: sound && pageCanPlay,
        onClick: () => open(item.threadId),
      });
      if (sound && (pageCanPlay || !shown)) playChime();
      if (!shown) {
        notification.open({
          key: `support-thread-${item.threadId}`,
          placement: 'bottomRight',
          icon: <img src={HP_LOGO_URL} alt="HP" width={32} height={32} style={{ borderRadius: 8 }} />,
          message: title,
          description: body,
          duration: 8,
          onClick: () => {
            open(item.threadId);
            notification.destroy(`support-thread-${item.threadId}`);
          },
          style: { cursor: 'pointer' },
        });
      }
    };

    const tick = async () => {
      if (busy.current || stopped) return;
      busy.current = true;
      try {
        const res: any = await AxiosUtils.get(
          API.supportNotifications,
          cursor.current === null ? {} : { after: cursor.current }
        );
        const data = res?.data || {};
        if (cursor.current === null) {
          // Lần đầu: chỉ lấy mốc, không báo lại tin cũ.
          cursor.current = Number(data.latestId) || 0;
          if (readShared() < cursor.current) writeShared(cursor.current);
          return;
        }
        const items: NoticeItem[] = data.items || [];
        if (items.length) {
          cursor.current = Math.max(cursor.current, ...items.map((i) => i.id));
          const shared = readShared();
          const fresh = items.filter((i) => i.id > shared);
          if (fresh.length) {
            writeShared(Math.max(...fresh.map((i) => i.id)));
            // Nhiều tin của cùng một người trong một nhịp → một popup (tin mới nhất).
            const latestPerThread = new Map<number, NoticeItem>();
            fresh.forEach((i) => latestPerThread.set(i.threadId, i));
            const me = meRef.current;
            const onlyMine = readNotifyScope() === 'mine';
            latestPerThread.forEach((item) => {
              if (!onlyMine || !item.assigneeId || item.assigneeId === me) notify(item);
            });
            refreshPendingCounts();
          }
        } else if (data.latestId) {
          cursor.current = Math.max(cursor.current, Number(data.latestId) || 0);
        }
      } catch {
        // Chưa có bảng hỗ trợ (503) / mất mạng: thử lại nhịp sau.
      } finally {
        busy.current = false;
      }
    };

    tick();
    const stop = startTicker(tick);
    return () => {
      stopped = true;
      stop();
    };
  }, []);

  return null;
};

export default SupportNotifier;
